// eslint-disable-next-line import/no-cycle
import assessmentService from '../../services/assessmentService';

export default {
  namespaced: true,

  state: {
    assessments: [],
    teamAssessments: [],
    minOfDatasetsForAggResults: 5, // Default value, will be fetched from backend
    currentAssessment: null,
    dirtyAssessmentsReport: null,
    systemOverview: null,
    statusAssessments: {},
    loading: false,
    error: null,
    duplicateCleaner: {
      duplicates: null,
      cleaningProposal: null,
      loading: false,
      cleaningInProgress: false,
    },
    undefinedChecker: {
      assessments: null,
      loading: false,
      fixingInProgress: false,
    },
  },

  mutations: {
    SET_ASSESSMENTS(state, assessments) {
      state.assessments = assessments;
    },
    MERGE_ASSESSMENTS(state, assessments) {
      state.assessments = [...state.assessments, ...assessments];
    },
    SET_CURRENT_ASSESSMENT(state, assessment) {
      state.currentAssessment = assessment;
    },
    CLEAR_CURRENT_ASSESSMENT(state) {
      state.currentAssessment = null;
    },
    ADD_ASSESSMENT(state, assessment) {
      state.assessments.push(assessment);
    },
    UPDATE_ASSESSMENT(state, updatedAssessment) {
      const index = state.assessments.findIndex((a) => a._id === updatedAssessment._id);
      if (index !== -1) {
        state.assessments.splice(index, 1, updatedAssessment);
      }
      state.currentAssessment = { ...updatedAssessment };
    },
    REMOVE_ASSESSMENT(state, assessmentId) {
      state.assessments = state.assessments.filter((a) => a._id !== assessmentId);
      if (state.currentAssessment && state.currentAssessment._id === assessmentId) {
        state.currentAssessment = null;
      }
    },
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_TEAM_ASSESSMENTS(state, teamAssessments) {
      state.teamAssessments = teamAssessments;
    },
    SET_MIN_OF_DATASETS_FOR_AGG_RESULTS(state, minOfDatasetsForAggResults) {
      state.minOfDatasetsForAggResults = minOfDatasetsForAggResults;
    },
    SET_SYSTEM_OVERVIEW(state, overview) {
      state.systemOverview = overview;
    },
    SET_STATUS_ASSESSMENTS(state, { status, assessments }) {
      state.statusAssessments[status] = assessments;
    },
    SET_DIRTY_ASSESSMENTS_REPORT(state, report) {
      state.dirtyAssessmentsReport = report;
    },
    SET_DUPLICATES(state, duplicates) {
      state.duplicateCleaner.duplicates = duplicates;
    },
    SET_CLEANING_PROPOSAL(state, proposal) {
      state.duplicateCleaner.cleaningProposal = proposal;
    },
    SET_DUPLICATE_LOADING(state, loading) {
      state.duplicateCleaner.loading = loading;
    },
    SET_CLEANING_IN_PROGRESS(state, inProgress) {
      state.duplicateCleaner.cleaningInProgress = inProgress;
    },
    SET_UNDEFINED_ASSESSMENTS(state, assessments) {
      state.undefinedChecker.assessments = assessments;
    },
    SET_UNDEFINED_LOADING(state, loading) {
      state.undefinedChecker.loading = loading;
    },
    SET_FIXING_IN_PROGRESS(state, inProgress) {
      state.undefinedChecker.fixingInProgress = inProgress;
    },
  },

  actions: {
    /**
     * Fetch all assessments
     * @param {Object} context - Vuex context
     */
    async fetchAssessments({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentService.getAllAssessments();
        commit('SET_ASSESSMENTS', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch assessments');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessments',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Fetch a specific assessment by ID
     * @param {Object} context - Vuex context
     * @param {string} assessmentId - ID of the assessment to fetch
     */
    async fetchAssessment({ commit, dispatch }, assessmentId) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentService.getAssessmentById(assessmentId);
        commit('SET_CURRENT_ASSESSMENT', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessment',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Create a new assessment
     * @param {Object} context - Vuex context
     * @param {Object} assessmentData - Data for the new assessment
     * @returns {Promise<Object|null>} Created assessment or null if failed
     */
    async createAssessment({ commit, dispatch }, assessmentData) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentService.createAssessment(assessmentData);
        commit('ADD_ASSESSMENT', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Assessment created successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to create assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to create assessment',
            severity: 'error',
          },
          { root: true },
        );
        return null;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Update an existing assessment
     * @param {Object} context - Vuex context
     * @param {Object} payload - Object containing id and update data
     * @param {string} payload.id - ID of the assessment to update
     * @param {Object} payload.data - Updated assessment data
     * @returns {Promise<Object|null>} Updated assessment or null if failed
     */
    async updateAssessment({ commit, dispatch }, { id, data }) {
      commit('SET_LOADING', true);
      try {
        const response = await assessmentService.updateAssessment(id, data);
        commit('UPDATE_ASSESSMENT', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Assessment updated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to update assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to update assessment',
            severity: 'error',
          },
          { root: true },
        );
        return null;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Delete an assessment
     * @param {Object} context - Vuex context
     * @param {string} assessmentId - ID of the assessment to delete
     */
    async deleteAssessment({ commit, dispatch }, assessmentId) {
      commit('SET_LOADING', true);
      try {
        await assessmentService.deleteAssessment(assessmentId);
        commit('REMOVE_ASSESSMENT', assessmentId);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Assessment deleted successfully',
            severity: 'success',
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to delete assessment');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to delete assessment',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Get assessment dimensions
     * @param {Object} context - Vuex context
     * @param {string} assessmentId - ID of the assessment
     * @returns {Promise<Array>} Array of dimensions
     */
    async getAssessmentDimensions({ dispatch }, assessmentId) {
      try {
        const response = await assessmentService.getAssessmentDimensions(assessmentId);
        return response.data;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessment dimensions',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get assessment charts
     * @param {Object} context - Vuex context
     * @param {string} assessmentId - ID of the assessment
     * @returns {Promise<Array>} Array of charts
     */
    async getAssessmentCharts({ dispatch }, assessmentId) {
      try {
        const response = await assessmentService.getAssessmentCharts(assessmentId);
        return response.data;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessment charts',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get linkable assessments
     * @param {Object} context - Vuex context
     * @param {Object} payload - Object containing assessmentId and dimensions
     * @param {string} payload.assessmentId - ID of the assessment
     * @param {Array<string>} payload.dimensions - Array of dimension IDs
     * @returns {Promise<Array>} Array of linkable assessments
     */
    async getLinkableAssessments({ dispatch }, { assessmentId, dimensions }) {
      try {
        const response = await assessmentService.getLinkableAssessments(assessmentId, dimensions);
        return response.data;
      } catch (error) {
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch linkable assessments',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get user assessment results
     * @param {Object} context - Vuex context
     * @param {Object} payload - Object containing userId and filter
     * @param {string} payload.userId - ID of the user
     * @param {Object} payload.filter - Filter parameters
     * @returns {Promise<Array>} Array of user assessment results
     */
    async getUserAssessmentsResults({ commit, dispatch }, { filter }) {
      try {
        const response = await assessmentService.getUserAssessmentsResults(filter);
        const filteredResponse = response.data;
        commit('SET_ASSESSMENTS', filteredResponse);
        return filteredResponse;
      } catch (error) {
        console.error('Failed to fetch user assessment results:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch user assessment results',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get user assessment results for My Results Dashboard
     * Uses dedicated dashboard endpoint that explicitly excludes external evaluation assessments
     * External evaluations (e.g., mindset360) are fetched separately
     * via getUserExternalEvaluationResults
     * @param {Object} context - Vuex context
     * @param {Object} payload - Object containing filter parameters
     * @param {Object} payload.filter - Filter parameters
     * @returns {Promise<Array>} Array of user assessment results excluding external evaluations
     */
    async getUserAssessmentsResultsForDashboard({ commit, dispatch }, { filter }) {
      try {
        const response = await assessmentService.getUserAssessmentsResultsForDashboard(filter);
        const assessments = response.data;
        commit('SET_ASSESSMENTS', assessments);
        return assessments;
      } catch (error) {
        console.error('Failed to fetch dashboard assessment results:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch dashboard assessment results',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get user external evaluation results
     * Generalized method that works with any external evaluation type
     * (identified by seriesId/createdFrom)
     * @param {Object} context - Vuex context
     * @param {Object} payload - Object containing filter parameters
     * @param {Object} payload.filter - Filter parameters
     * @param {string} payload.filter.schemaType - Optional: external evaluation schema type
     * @returns {Promise<Array>} Array of user external evaluation results
     */
    async getUserExternalEvaluationResults({ commit, dispatch }, { filter }) {
      try {
        const response = await assessmentService.getUserExternalEvaluationResults(filter);
        commit('MERGE_ASSESSMENTS', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch user external evaluation results:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch external evaluation results',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get team assessment results
     * @param {Object} context - Vuex context
     * @param {Object} payload - Object containing teamMembers and status
     * @param {Array} payload.teamMembers - Array of team member IDs
     * @param {string} payload.status - Assessment status filter
     * @returns {Promise<Array>} Array of team assessment results
     */
    async getTeamAssessmentsResults({ commit, dispatch }, { teamMembers, status }) {
      try {
        const response = await assessmentService.getUserTeamAssessmentsResults(
          { teamMembers, status },
        );
        // Handle new response structure with assessments and minOfDatasetsForAggResults
        const { assessments, minOfDatasetsForAggResults } = response.data;
        commit('SET_TEAM_ASSESSMENTS', assessments);
        if (minOfDatasetsForAggResults !== undefined) {
          commit('SET_MIN_OF_DATASETS_FOR_AGG_RESULTS', minOfDatasetsForAggResults);
        }
        return assessments;
      } catch (error) {
        console.error('Failed to fetch team assessment results:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch team assessment results',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get assessments by user ID
     * @param {Object} context - Vuex context
     * @param {string} userId - ID of the user
     */
    async getAssessmentsByUserId({ commit, dispatch }, userId) {
      try {
        const response = await assessmentService.getAssessmentsByUserId(userId);
        commit('SET_ASSESSMENTS', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch assessments by user ID:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch user assessments',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get system assessment overview
     * @param {Object} context - Vuex context
     */
    async getSystemAssessmentOverview({ commit, dispatch }) {
      try {
        const response = await assessmentService.getSystemAssessmentOverview();
        commit('SET_SYSTEM_OVERVIEW', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch system assessment overview:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch system overview',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get assessments by status
     * @param {Object} context - Vuex context
     * @param {string} status - Assessment status
     */
    async getAssessmentsByStatus({ commit, dispatch }, status) {
      try {
        const response = await assessmentService.getAssessmentsByStatus(status);
        commit('SET_STATUS_ASSESSMENTS', { status, assessments: response.data });
        return response.data;
      } catch (error) {
        console.error('Failed to fetch assessments by status:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessments by status',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get dirty assessments report
     * @param {Object} context - Vuex context
     */
    async getDirtyAssessmentsReport({ commit, dispatch }) {
      try {
        const response = await assessmentService.getDirtyAssessmentsReport();
        commit('SET_DIRTY_ASSESSMENTS_REPORT', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch dirty assessments report:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch dirty assessments report',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Fetch all assessments for a filter (all pages)
     * @param {Object} context - Vuex context
     * @param {Object} payload - Filter parameters
     * @param {string} payload.status - Optional status filter
     * @param {string} payload.filterType - Optional filter type
     * @returns {Promise<Array>} Array of all assessments matching the filter
     */
    async fetchAllAssessmentsForFilter({ dispatch }, { status, filterType }) {
      try {
        const allAssessments = [];
        const limit = 100;
        const pagePromises = [];

        // First, get the first page to determine total pages
        const firstPageParams = { page: 1, limit };
        if (status) firstPageParams.status = status;
        if (filterType) firstPageParams.filterType = filterType;

        const firstResponse = await assessmentService.getAllAssessmentsPaginated(firstPageParams);
        const { data: firstPageData, pagination } = firstResponse.data;
        allAssessments.push(...firstPageData);

        // Fetch remaining pages in parallel if there are more
        if (pagination.totalPages > 1) {
          for (let currentPage = 2; currentPage <= pagination.totalPages; currentPage += 1) {
            const params = { page: currentPage, limit };
            if (status) params.status = status;
            if (filterType) params.filterType = filterType;
            pagePromises.push(assessmentService.getAllAssessmentsPaginated(params));
          }

          const remainingResponses = await Promise.all(pagePromises);
          remainingResponses.forEach((response) => {
            allAssessments.push(...response.data.data);
          });
        }

        return allAssessments;
      } catch (error) {
        console.error('Failed to fetch all assessments for filter:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessments',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Bulk delete all assessments for a filter
     * @param {Object} context - Vuex context
     * @param {Object} payload - Filter parameters
     * @param {string} payload.status - Optional status filter
     * @param {string} payload.filterType - Optional filter type
     * @returns {Promise<Object>} Result with success and failure counts
     */
    async bulkDeleteAllAssessmentsForFilter({ dispatch, commit }, { status, filterType }) {
      try {
        // Fetch all assessments for the filter
        const allAssessments = await dispatch('fetchAllAssessmentsForFilter', { status, filterType });

        if (allAssessments.length === 0) {
          return { successCount: 0, failCount: 0, total: 0 };
        }

        // Delete all assessments in parallel
        const deletePromises = allAssessments.map(async (assessment) => {
          try {
            await assessmentService.deleteAssessment(assessment._id);
            commit('REMOVE_ASSESSMENT', assessment._id);
            return { success: true, assessment };
          } catch (error) {
            console.error(`Failed to delete assessment ${assessment._id}:`, error);
            return { success: false, assessment, error };
          }
        });

        const results = await Promise.all(deletePromises);
        const successCount = results.filter((r) => r.success).length;
        const failCount = results.filter((r) => !r.success).length;

        return { successCount, failCount, total: allAssessments.length };
      } catch (error) {
        console.error('Failed to bulk delete assessments:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to delete assessments',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    /**
     * Get paginated assessments
     * @param {Object} context - Vuex context
     * @param {Object} payload - Pagination and filter parameters
     * @param {number} payload.page - Page number
     * @param {number} payload.limit - Items per page
     * @param {string} payload.status - Optional status filter
     * @param {string} payload.filterType - Optional filter type
     * @param {string} payload.userId - Optional user ID filter
     * @param {string} payload.startDate - Optional start date (ISO string)
     * @param {string} payload.endDate - Optional end date (ISO string)
     * @param {string} payload.assessmentType - Optional assessment type filter
     * @returns {Promise<Object>} Paginated assessments with pagination metadata
     */
    async getPaginatedAssessments({ dispatch }, {
      page,
      limit,
      status,
      filterType,
      userId,
      startDate,
      endDate,
      assessmentType,
      userRoles,
    }) {
      try {
        const params = { page, limit };
        if (status) params.status = status;
        if (filterType) params.filterType = filterType;
        if (userId) params.userId = userId;
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (assessmentType) params.assessmentType = assessmentType;
        if (userRoles) params.userRoles = userRoles;

        const response = await assessmentService.getAllAssessmentsPaginated(params);
        return {
          data: response.data.data,
          pagination: response.data.pagination,
        };
      } catch (error) {
        console.error('Failed to fetch paginated assessments:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch assessments',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      }
    },

    async findDuplicateAssessments({ commit, dispatch }, timeWindowHours = 24) {
      try {
        commit('SET_DUPLICATE_LOADING', true);
        const response = await assessmentService.findDuplicateAssessments(timeWindowHours);
        commit('SET_DUPLICATES', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to find duplicate assessments:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to find duplicate assessments',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_DUPLICATE_LOADING', false);
      }
    },

    async proposeDuplicateCleaning({ commit, dispatch }, params = {}) {
      try {
        commit('SET_DUPLICATE_LOADING', true);
        // Support both old format (number) and new format (object)
        const requestParams = typeof params === 'number'
          ? { timeWindowHours: params }
          : {
            timeWindowHours: params.timeWindowHours || 24,
            assessmentType: params.assessmentType || null,
          };
        const response = await assessmentService.proposeDuplicateCleaning(requestParams);
        commit('SET_CLEANING_PROPOSAL', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to get cleaning proposal:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to get cleaning proposal',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_DUPLICATE_LOADING', false);
      }
    },

    async executeDuplicateCleaning({ commit, dispatch }, assessmentIds) {
      try {
        commit('SET_CLEANING_IN_PROGRESS', true);
        const response = await assessmentService.executeDuplicateCleaning(assessmentIds);
        const results = response.data;

        // Remove deleted assessments from state
        assessmentIds.forEach((id) => {
          commit('REMOVE_ASSESSMENT', id);
        });

        // Show success message
        dispatch(
          'notifications/showToast',
          {
            detail: `Successfully deleted ${results.deleted} assessment(s)`,
            severity: 'success',
          },
          { root: true },
        );

        if (results.failed > 0) {
          dispatch(
            'notifications/showToast',
            {
              detail: `Failed to delete ${results.failed} assessment(s)`,
              severity: 'warn',
            },
            { root: true },
          );
        }

        return results;
      } catch (error) {
        console.error('Failed to execute cleaning:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to execute cleaning',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_CLEANING_IN_PROGRESS', false);
      }
    },

    async findUndefinedDimensions({ commit, dispatch }) {
      try {
        commit('SET_UNDEFINED_LOADING', true);
        const response = await assessmentService.findUndefinedDimensions();
        commit('SET_UNDEFINED_ASSESSMENTS', response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to find undefined dimensions:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to find assessments with undefined dimensions',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_UNDEFINED_LOADING', false);
      }
    },

    async assignDimensionsToUndefined({ commit, dispatch }, assessmentIds) {
      try {
        commit('SET_FIXING_IN_PROGRESS', true);
        const response = await assessmentService.assignDimensionsToUndefined(assessmentIds);
        const results = response.data;

        // Show success message
        dispatch(
          'notifications/showToast',
          {
            detail: `Successfully assigned dimensions to ${results.assigned || assessmentIds.length} assessment(s)`,
            severity: 'success',
          },
          { root: true },
        );

        if (results.failed > 0) {
          dispatch(
            'notifications/showToast',
            {
              detail: `Failed to assign dimensions to ${results.failed} assessment(s)`,
              severity: 'warn',
            },
            { root: true },
          );
        }

        // Reload undefined dimensions to refresh the view
        await dispatch('findUndefinedDimensions');

        return results;
      } catch (error) {
        console.error('Failed to assign dimensions:', error);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to assign dimensions to undefined assessments',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_FIXING_IN_PROGRESS', false);
      }
    },
  },

  getters: {
    /**
     * Get assessment by ID
     * @param {Object} state - Vuex state
     * @returns {Function} Function that takes an ID and returns the assessment
     */
    getAssessmentById: (state) => (id) =>
      // eslint-disable-next-line implicit-arrow-linebreak
      state.assessments.find((assessment) => assessment._id === id),
    currentAssessment: (state) => state.currentAssessment,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    systemOverview: (state) => state.systemOverview,
    statusAssessments: (state) => state.statusAssessments,
    dirtyAssessmentsReport: (state) => state.dirtyAssessmentsReport,
    duplicateCleaner: (state) => state.duplicateCleaner,
    undefinedChecker: (state) => state.undefinedChecker,
  },
};
